/**
 * Border styles for the "ohey times" newsletter board.
 *
 * "classic" keeps the original Figma band PNGs (wired up in Newsletter.js).
 * Every other style is drawn in code as SVG data-URI tiles — same mechanism
 * (board background images: top band, bottom band, left/right strips), no
 * design assets needed, crisp in the html2canvas PDF capture. Drafts saved
 * with a retired style key fall back to classic via borderStyleByKey.
 */

const ORANGE = '#EF5D34'
const SOFT = '#F5A066'
const TEAL = '#1E8A6E'
const MUSTARD = '#F0C24C'
const STEEL = '#7FA8C9'
const INK = '#39393B'

const uri = (w, h, body) =>
  `url("data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${h}' viewBox='0 0 ${w} ${h}'>${body}</svg>`
  )}")`

/* ------------------------------ shapes ------------------------------ */

const dot = (x, y, r, color) =>
  `<circle cx='${x}' cy='${y}' r='${r}' fill='${color}'/>`

// Four-point sparkle star.
const sparkle = (x, y, s, color) =>
  `<path d='M${x},${y - s} q3,${s - 8} ${s},${s} q-${s - 8},3 -${s},${s} q-3,-${
    s - 8
  } -${s},-${s} q${s - 8},-3 ${s},-${s} Z' fill='${color}'/>`

// Open ring (bubble).
const ring = (x, y, r, color, sw = 10) =>
  `<circle cx='${x}' cy='${y}' r='${r}' stroke='${color}' stroke-width='${sw}' fill='none'/>`

// Scallop run — semicircle domes marching along y. sweep 1 = domes point up,
// sweep 0 = domes hang down (for the top edge).
const scallops = (y, r, n, colors, sweep = 1) => {
  let run = ''
  for (let i = 0; i < n; i += 1) {
    const c = colors[i % colors.length]
    const cx = r + i * 2 * r
    run += `<path d='M${cx - r},${y} a${r},${r} 0 0 ${sweep} ${
      2 * r
    },0 Z' fill='${c}'/>`
  }
  return run
}

// Sideways scallop — one dome pointing into the page from a strip edge.
const scallopSide = (x, y, r, color, angle) =>
  `<path d='M${x - r},${y} a${r},${r} 0 0 1 ${
    2 * r
  },0 Z' fill='${color}' transform='rotate(${angle} ${x} ${y})'/>`

// Daisy — six petal circles around a centre dot.
const flower = (x, y, s, petal, centre) => {
  let petals = ''
  for (let i = 0; i < 6; i += 1) {
    const a = i * 60 * (Math.PI / 180)
    petals += `<circle cx='${x + Math.cos(a) * s}' cy='${
      y + Math.sin(a) * s
    }' r='${s * 0.62}' fill='${petal}'/>`
  }
  return `${petals}<circle cx='${x}' cy='${y}' r='${
    s * 0.55
  }' fill='${centre}'/>`
}

// Pennant flag hanging off a bunting string (dir 1 = down, -1 = up).
const pennant = (x, y, color, dir = 1) =>
  `<path d='M${x - 16},${y} l32,0 l-16,${34 * dir} Z' fill='${color}'/>`

/* ------------------------------ bands ------------------------------ */

// Horizontal tiles are 560px wide; vertical strips 64px wide, 560px tall.
const band = (body) => uri(560, 150, body)
const strip = (body) => uri(64, 560, body)

const sparkleTop = band(
  sparkle(60, 80, 30, MUSTARD) +
    dot(140, 60, 8, STEEL) +
    sparkle(220, 100, 22, ORANGE) +
    sparkle(330, 60, 34, TEAL) +
    dot(420, 100, 10, SOFT) +
    sparkle(500, 80, 26, STEEL)
)
const sparkleBottom = band(
  sparkle(80, 90, 26, TEAL) +
    dot(170, 60, 9, MUSTARD) +
    sparkle(270, 80, 32, SOFT) +
    dot(370, 100, 8, ORANGE) +
    sparkle(470, 70, 24, MUSTARD)
)
const sparkleSide = strip(
  sparkle(32, 70, 24, ORANGE) +
    dot(28, 180, 8, TEAL) +
    sparkle(34, 300, 28, MUSTARD) +
    dot(30, 410, 9, STEEL) +
    sparkle(32, 500, 22, SOFT)
)

const bubblesTop = band(
  ring(70, 80, 30, TEAL) +
    dot(150, 60, 12, SOFT) +
    ring(240, 90, 22, MUSTARD, 8) +
    dot(330, 70, 9, STEEL) +
    ring(420, 80, 34, ORANGE) +
    dot(510, 100, 14, TEAL)
)
const bubblesBottom = band(
  dot(60, 80, 13, MUSTARD) +
    ring(150, 70, 26, STEEL, 8) +
    dot(250, 90, 9, ORANGE) +
    ring(350, 80, 32, TEAL) +
    dot(450, 60, 12, SOFT) +
    ring(520, 90, 20, MUSTARD, 8)
)
const bubblesSide = strip(
  ring(32, 70, 22, ORANGE, 8) +
    dot(30, 170, 10, TEAL) +
    ring(34, 280, 26, MUSTARD, 8) +
    dot(28, 390, 9, SOFT) +
    ring(32, 490, 20, STEEL, 8)
)

// Scalloped edges — domes hang from the top edge and rise from the bottom
// edge (14 domes × 40px = 560, so the tile repeats seamlessly).
const SCALLOP_COLORS = [ORANGE, MUSTARD, TEAL, SOFT, STEEL]
const scallopTop = band(
  scallops(0, 20, 14, SCALLOP_COLORS, 0) +
    dot(90, 90, 9, TEAL) +
    dot(280, 100, 11, ORANGE) +
    dot(470, 85, 8, MUSTARD)
)
const scallopBottom = band(
  scallops(150, 20, 14, SCALLOP_COLORS, 1) +
    dot(120, 60, 9, STEEL) +
    dot(320, 50, 10, SOFT) +
    dot(500, 65, 8, TEAL)
)
const scallopSideStrip = strip(
  scallopSide(0, 56, 20, ORANGE, 90) +
    scallopSide(0, 168, 20, MUSTARD, 90) +
    scallopSide(0, 280, 20, TEAL, 90) +
    scallopSide(0, 392, 20, SOFT, 90) +
    scallopSide(0, 504, 20, STEEL, 90) +
    dot(48, 112, 7, TEAL) +
    dot(48, 336, 7, ORANGE)
)

const flowersTop = band(
  flower(70, 80, 22, ORANGE, MUSTARD) +
    dot(150, 60, 8, TEAL) +
    flower(240, 90, 17, STEEL, SOFT) +
    dot(330, 65, 9, MUSTARD) +
    flower(430, 75, 24, TEAL, ORANGE) +
    dot(520, 100, 8, SOFT)
)
const flowersBottom = band(
  flower(90, 80, 19, MUSTARD, ORANGE) +
    dot(180, 100, 8, STEEL) +
    flower(290, 70, 23, SOFT, TEAL) +
    dot(390, 90, 9, ORANGE) +
    flower(490, 85, 18, TEAL, MUSTARD)
)
const flowersSide = strip(
  flower(32, 70, 18, ORANGE, MUSTARD) +
    dot(30, 165, 7, TEAL) +
    flower(32, 280, 20, TEAL, SOFT) +
    dot(34, 395, 8, MUSTARD) +
    flower(32, 490, 17, STEEL, ORANGE)
)

// Bunting — pennant strings; the string dips are quadratic curves whose
// mid-points carry the flags, and both tile ends sit at the same y so the
// band repeats seamlessly.
const buntingTop = band(
  `<path d='M0,30 Q140,80 280,30 Q420,80 560,30' stroke='${INK}' stroke-width='4' fill='none'/>` +
    pennant(70, 49, ORANGE) +
    pennant(140, 55, MUSTARD) +
    pennant(210, 49, TEAL) +
    pennant(350, 49, SOFT) +
    pennant(420, 55, STEEL) +
    pennant(490, 49, ORANGE)
)
const buntingBottom = band(
  `<path d='M0,120 Q140,70 280,120 Q420,70 560,120' stroke='${INK}' stroke-width='4' fill='none'/>` +
    pennant(70, 101, TEAL, -1) +
    pennant(140, 95, ORANGE, -1) +
    pennant(210, 101, MUSTARD, -1) +
    pennant(350, 101, STEEL, -1) +
    pennant(420, 95, SOFT, -1) +
    pennant(490, 101, TEAL, -1)
)
const buntingSide = strip(
  `<path d='M24,0 Q60,140 24,280 Q-12,420 24,560' stroke='${INK}' stroke-width='4' fill='none'/>` +
    `<path d='M42,124 l0,32 l30,-16 Z' fill='${ORANGE}'/>` +
    `<path d='M24,264 l0,32 l30,-16 Z' fill='${MUSTARD}'/>` +
    `<path d='M6,404 l0,32 l30,-16 Z' fill='${TEAL}'/>`
)

/* ------------------------------ styles ------------------------------ */

// `bands` is null for classic — Newsletter.js keeps its original PNG set.
// `card` overrides the white card's frame so the whole page reads as one
// style. Paddings stay identical so content layout never shifts.
const BORDER_STYLES = [
  {
    key: 'classic',
    label: 'Classic (orange bands)',
    bands: null,
    card: { border: `4px solid ${ORANGE}`, borderRadius: 44 }
  },
  {
    key: 'sparkles',
    label: 'Sparkles',
    bands: { top: sparkleTop, bottom: sparkleBottom, side: sparkleSide },
    card: { border: `4px solid ${TEAL}`, borderRadius: 44 }
  },
  {
    key: 'bubbles',
    label: 'Bubbles',
    bands: { top: bubblesTop, bottom: bubblesBottom, side: bubblesSide },
    card: { border: `4px solid ${STEEL}`, borderRadius: 44 }
  },
  {
    key: 'scallops',
    label: 'Scallops',
    bands: { top: scallopTop, bottom: scallopBottom, side: scallopSideStrip },
    card: { border: `4px solid ${MUSTARD}`, borderRadius: 44 }
  },
  {
    key: 'flowers',
    label: 'Flowers',
    bands: { top: flowersTop, bottom: flowersBottom, side: flowersSide },
    card: { border: `4px solid ${SOFT}`, borderRadius: 44 }
  },
  {
    key: 'bunting',
    label: 'Bunting flags',
    bands: { top: buntingTop, bottom: buntingBottom, side: buntingSide },
    card: { border: `4px solid ${ORANGE}`, borderRadius: 44 }
  }
]

const borderStyleByKey = (key) =>
  BORDER_STYLES.find((s) => s.key === key) || BORDER_STYLES[0]

export { BORDER_STYLES, borderStyleByKey }
